import { assertRuntimeClient, VertcieRuntimeElement } from "@vertcie/runtime-core";
import { createExpanderSession } from "./session.js";

export const EXPANDERS_VERSION = "0.1.16";
export const EXPANDERS_ELEMENT = "vertcie-expanders";
export const EXPANDERS_CLIENT = assertRuntimeClient("expanders");
export { createExpanderSession } from "./session.js";

const GSAP_MODULE_URL = "https://esm.sh/gsap@3.13.0?bundle";
const SHADER_PAGE_SIZE = 12;
let gsapPromise = null;

function loadGsap() {
  if (!gsapPromise) {
    gsapPromise = import(GSAP_MODULE_URL)
      .then((module) => module.gsap ?? module.default ?? module)
      .catch((reason) => {
        gsapPromise = null;
        throw reason;
      });
  }
  return gsapPromise;
}

function prefersReducedMotion() {
  return globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

async function animateCollapsible(element, opening) {
  if (!element || prefersReducedMotion()) return;

  try {
    const gsap = await loadGsap();
    gsap.killTweensOf(element);

    await new Promise((resolve) => {
      if (opening) {
        const targetHeight = element.scrollHeight;
        gsap.fromTo(
          element,
          { height: 0, opacity: 0, overflow: "hidden" },
          {
            height: targetHeight,
            opacity: 1,
            duration: 0.36,
            ease: "power2.out",
            overwrite: "auto",
            onComplete: () => {
              gsap.set(element, { height: "auto", clearProps: "overflow" });
              resolve();
            },
          },
        );
        return;
      }

      gsap.fromTo(
        element,
        { height: element.getBoundingClientRect().height, opacity: 1, overflow: "hidden" },
        {
          height: 0,
          opacity: 0,
          duration: 0.3,
          ease: "power2.inOut",
          overwrite: "auto",
          onComplete: resolve,
        },
      );
    });
  } catch {
    // Opening and closing remain functional if the optional animation module is unavailable.
  }
}

const runtimeStyles = `
  .expander-options {
    will-change: height, opacity;
  }
  .expander-option {
    grid-template-columns: 18px minmax(0, 1fr);
  }
  .expander-shader-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(48px, 1fr));
    gap: 10px;
    width: 100%;
  }
  .expander-family {
    width: 100%;
    min-height: 38px;
    margin-bottom: 12px;
    padding: 7px 32px 7px 10px;
    border: 1px solid var(--vtx-line, #eee8de);
    border-radius: 6px;
    background: var(--vtx-surface, #fff);
    color: var(--vtx-ink, #181512);
    font: inherit;
    font-size: 13px;
    cursor: pointer;
  }
  .expander-family:focus-visible {
    outline: 2px solid var(--vtx-accent, #7b7269);
    outline-offset: 2px;
  }
  .expander-family-expanders {
    display: grid;
    gap: 1px;
    margin-bottom: 12px;
    border: 1px solid var(--vtx-line, #eee8de);
    border-radius: 6px;
    overflow: hidden;
  }
  .expander-family-expander {
    min-height: 38px;
    padding: 8px 10px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 18px;
    align-items: center;
    border: 0;
    border-bottom: 1px solid var(--vtx-line, #eee8de);
    background: var(--vtx-surface, #fff);
    color: var(--vtx-ink, #181512);
    font: inherit;
    font-size: 13px;
    text-align: left;
    cursor: pointer;
  }
  .expander-family-expander:last-child { border-bottom: 0; }
  .expander-family-expander:hover,
  .expander-family-expander[aria-expanded="true"] { background: var(--vtx-hover, #faf8f4); }
  .expander-family-expander:focus-visible {
    position: relative;
    outline: 2px solid var(--vtx-accent, #7b7269);
    outline-offset: -2px;
  }
  .expander-family-expander-chevron {
    width: 16px;
    height: 16px;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.6;
    transition: transform .15s;
  }
  .expander-family-expander[aria-expanded="true"] .expander-family-expander-chevron {
    transform: rotate(180deg);
  }
  .expander-shader-option {
    position: relative;
    min-height: 52px;
    display: grid;
    place-items: center;
    padding: 2px;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: var(--vtx-ink, #181512);
    cursor: pointer;
  }
  .expander-shader-option:focus-visible {
    outline: 2px solid var(--vtx-accent, #7b7269);
    outline-offset: 3px;
  }
  .expander-shader-option[aria-pressed="true"] .expander-swatch {
    border-color: var(--vtx-accent, #7b7269);
    box-shadow: 0 0 0 2px var(--vtx-surface, #fff), 0 0 0 4px var(--vtx-accent, #7b7269);
  }
  .expander-shader-check {
    position: absolute;
    right: 1px;
    bottom: 1px;
    width: 18px;
    height: 18px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: var(--vtx-ink, #181512);
    color: var(--vtx-surface, #fff);
    font-size: 11px;
    line-height: 1;
  }
  .expander-swatch {
    width: 44px;
    height: 44px;
    display: block;
    overflow: hidden;
    border: 1px solid var(--vtx-line, #eee8de);
    border-radius: 50%;
    background: var(--vtx-hover, #faf8f4);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.24);
  }
  .expander-swatch img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }
  .expander-swatch[data-loading="true"] {
    animation: vtx-swatch-pulse 1.2s ease-in-out infinite alternate;
  }
  .expander-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding-top: 12px;
  }
  .expander-pagination-label {
    min-width: 70px;
    color: var(--vtx-muted, #72685d);
    font-size: 11px;
    text-align: center;
  }
  .expander-pagination-button {
    min-height: 30px;
    padding: 5px 11px;
    border: 1px solid var(--vtx-line, #eee8de);
    border-radius: 999px;
    background: transparent;
    color: var(--vtx-ink, #181512);
    font: inherit;
    font-size: 11px;
    cursor: pointer;
  }
  .expander-pagination-button:disabled {
    opacity: 0.38;
    cursor: default;
  }
  .expander-tooltip {
    position: fixed;
    z-index: 1000;
    max-width: 220px;
    padding: 6px 9px;
    border-radius: 5px;
    background: var(--vtx-ink, #181512);
    color: var(--vtx-surface, #fff);
    font-size: 11px;
    line-height: 1.35;
    pointer-events: none;
    opacity: 0;
    transform: translate(-50%, -4px);
    transition: opacity 120ms ease, transform 120ms ease;
  }
  .expander-tooltip[data-visible="true"] {
    opacity: 1;
    transform: translate(-50%, -8px);
  }
  @keyframes vtx-swatch-pulse {
    from { opacity: 0.45; }
    to { opacity: 0.9; }
  }
  @media (prefers-reduced-motion: reduce) {
    .expander-swatch[data-loading="true"] { animation: none; }
  }
`;

export class VertcieExpandersElement extends VertcieRuntimeElement {
  #session = null;
  #baseSelectedShaders = [];
  #openBranchId = null;
  #configuration = null;
  #shaderRequests = new Map();
  #assetUrlRequests = new Map();
  #pageByBranch = new Map();
  #familyByBranch = new Map();
  #lazySwatchEntries = new WeakMap();
  #lazySwatchObserver = null;
  #tooltip = null;
  #transition = 0;

  constructor() { super("expanders"); }

  get state() { return this.#session?.state ?? null; }

  async initialize(configuration) {
    this.#configuration = configuration;
    return super.initialize(configuration);
  }

  #subMode(presentation) {
    const requested = this.getAttribute("sub-mode")
      ?? this.#configuration?.subMode
      ?? presentation?.subs?.mode
      ?? "dropdown";
    return ["dropdown", "expanders", "none"].includes(requested) ? requested : "dropdown";
  }

  async #resolveShader(manifest, shaderId) {
    let request = this.#shaderRequests.get(shaderId);
    if (request) return request;

    request = (async () => {
      if (typeof this.#configuration?.resolveShader === "function") {
        return this.#configuration.resolveShader(shaderId);
      }
      const endpoint = this.#configuration?.endpoint?.replace(/\/$/, "") ?? globalThis.location?.origin;
      if (!endpoint) throw new Error("Runtime endpoint is required for shader swatches.");
      const path = `/api/runtime/v2/publications/${encodeURIComponent(manifest.publishedFileId)}/revisions/${manifest.revision}/clients/${manifest.client}/shaders/${encodeURIComponent(shaderId)}`;
      const response = await fetch(`${endpoint}${path}`, {
        credentials: "include",
        headers: {
          Accept: "application/json",
          ...(this.#configuration?.apiKey ? { "x-vertcie-key": this.#configuration.apiKey } : {}),
        },
      });
      if (!response.ok) throw new Error(`Runtime shader request failed (${response.status}).`);
      return response.json();
    })();

    this.#shaderRequests.set(shaderId, request);
    request.catch(() => this.#shaderRequests.delete(shaderId));
    return request;
  }

  async #resolveAssetUrl(path, color) {
    if (/^https?:\/\//i.test(path)) return path;
    let request = this.#assetUrlRequests.get(path);
    if (request) return request;

    request = (async () => {
      const endpoint = this.#configuration?.endpoint?.replace(/\/$/, "") ?? globalThis.location?.origin;
      if (!endpoint) throw new Error("Runtime endpoint is required for shader swatches.");
      const url = new URL("/api/get-signed-url", `${endpoint}/`);
      url.searchParams.set("path", path);
      const response = await fetch(url, {
        credentials: "include",
        headers: {
          Accept: "application/json",
          ...(this.#configuration?.apiKey ? { "x-vertcie-key": this.#configuration.apiKey } : {}),
        },
      });
      if (response.ok) {
        const payload = await response.json();
        if (typeof payload?.url === "string" && payload.url) return payload.url;
      }

      const fallback = new URL("/api/shader-swatch", `${endpoint}/`);
      fallback.searchParams.set("map", path);
      if (typeof color === "string" && color) fallback.searchParams.set("color", color);
      fallback.searchParams.set("v", "runtime-v2-20260726");
      return fallback.href;
    })();

    this.#assetUrlRequests.set(path, request);
    request.catch(() => this.#assetUrlRequests.delete(path));
    return request;
  }

  #ensureLazySwatchObserver(root) {
    this.#lazySwatchObserver?.disconnect();
    this.#lazySwatchObserver = null;
    if (typeof IntersectionObserver !== "function") return;

    this.#lazySwatchObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        this.#activateLazySwatch(entry.target);
      }
    }, {
      root,
      rootMargin: "40px 0px",
      threshold: 0.01,
    });
  }

  #queueShaderSwatch(manifest, shaderId, swatch) {
    this.#lazySwatchEntries.set(swatch, { manifest, shaderId });
    swatch.dataset.loading = "true";
    if (this.#lazySwatchObserver) {
      this.#lazySwatchObserver.observe(swatch);
    } else {
      void this.#activateLazySwatch(swatch);
    }
  }

  async #activateLazySwatch(swatch) {
    const entry = this.#lazySwatchEntries.get(swatch);
    if (!entry || !swatch.isConnected) return;
    this.#lazySwatchObserver?.unobserve(swatch);
    this.#lazySwatchEntries.delete(swatch);
    await this.#renderShaderSwatch(entry.manifest, entry.shaderId, swatch);
  }

  #ensureTooltip() {
    if (this.#tooltip?.isConnected) return this.#tooltip;
    const tooltip = document.createElement("div");
    tooltip.className = "expander-tooltip";
    tooltip.setAttribute("role", "tooltip");
    tooltip.id = `vtx-expander-tooltip-${Math.random().toString(36).slice(2)}`;
    this.shadowRoot?.append(tooltip);
    this.#tooltip = tooltip;
    return tooltip;
  }

  #showTooltip(label, anchor) {
    if (!label || !anchor?.isConnected) return;
    const tooltip = this.#ensureTooltip();
    const bounds = anchor.getBoundingClientRect();
    tooltip.textContent = label;
    tooltip.style.left = `${bounds.left + bounds.width / 2}px`;
    tooltip.style.top = `${Math.max(8, bounds.top - 4)}px`;
    tooltip.dataset.visible = "true";
    anchor.setAttribute("aria-describedby", tooltip.id);
  }

  #hideTooltip(anchor = null) {
    if (this.#tooltip) delete this.#tooltip.dataset.visible;
    anchor?.removeAttribute("aria-describedby");
  }

  async #renderShaderSwatch(manifest, shaderId, swatch) {
    try {
      const shader = await this.#resolveShader(manifest, shaderId);
      const material = shader?.material && typeof shader.material === "object" ? shader.material : {};
      const swatchAsset = material.swatch && typeof material.swatch === "object" ? material.swatch : {};
      if (typeof material.color === "string" && material.color) {
        swatch.style.backgroundColor = material.color;
      }
      const signedSource = [
        swatchAsset.thumbnailUrl,
        material.mapUrl,
        material.diffuseMapUrl,
        material.previewUrl,
      ].find((value) => typeof value === "string" && value.trim());
      const source = [material.map, material.diffuseMap, material.preview]
        .find((value) => typeof value === "string" && value.trim());
      if (!signedSource && !source) return;
      const image = document.createElement("img");
      image.alt = "";
      image.decoding = "async";
      image.loading = "lazy";
      image.addEventListener("error", () => image.remove(), { once: true });
      image.src = signedSource ?? await this.#resolveAssetUrl(source, material.color);
      if (typeof swatchAsset.detailUrl === "string" && swatchAsset.detailUrl) {
        image.dataset.thumbnailSrc = image.src;
        image.dataset.detailSrc = swatchAsset.detailUrl;
      }
      swatch.replaceChildren(image);
    } catch {
      // Keep the material color or neutral placeholder when a preview is unavailable.
    } finally {
      delete swatch.dataset.loading;
    }
  }

  #buildPagination(branchId, pageIndex, totalItems, onChange) {
    const pageCount = Math.max(1, Math.ceil(totalItems / SHADER_PAGE_SIZE));
    const start = totalItems === 0 ? 0 : pageIndex * SHADER_PAGE_SIZE + 1;
    const end = Math.min(totalItems, (pageIndex + 1) * SHADER_PAGE_SIZE);
    const navigation = document.createElement("nav");
    navigation.className = "expander-pagination";
    navigation.setAttribute("aria-label", "Material swatch pages");

    const previous = document.createElement("button");
    previous.type = "button";
    previous.className = "expander-pagination-button";
    previous.textContent = "Previous";
    previous.disabled = pageIndex === 0;
    previous.addEventListener("click", () => {
      this.#pageByBranch.set(branchId, pageIndex - 1);
      onChange();
    });

    const label = document.createElement("span");
    label.className = "expander-pagination-label";
    label.textContent = `${start}–${end} of ${totalItems}`;
    label.setAttribute("aria-live", "polite");

    const next = document.createElement("button");
    next.type = "button";
    next.className = "expander-pagination-button";
    next.textContent = "Next";
    next.disabled = pageIndex >= pageCount - 1;
    next.addEventListener("click", () => {
      this.#pageByBranch.set(branchId, pageIndex + 1);
      onChange();
    });

    navigation.append(previous, label, next);
    return navigation;
  }

  async renderManifest(manifest, presentation) {
    const branches = manifest.initialState?.branches;
    if (!Array.isArray(branches) || branches.length === 0) throw new Error("Expander manifest branches are required.");
    this.#baseSelectedShaders = Array.isArray(manifest.initialState.baseSelectedShaders) ? manifest.initialState.baseSelectedShaders : [];
    this.#session = createExpanderSession({ branches, initialSelections: manifest.initialState.selections });
    const selectedShaders = () => [...new Set([...this.#baseSelectedShaders, ...this.#session.state.selectedShaders])];
    const root = await this.renderRuntime(manifest, this.#session.state.selectedMeshes, presentation, selectedShaders(), this.#session.state.selectedTransforms);
    const subMode = this.#subMode(presentation);

    await this.applyRuntimeSelection(
      this.#session.state.selectedMeshes,
      selectedShaders(),
      this.#session.state.selectedTransforms,
    );
    this.shadowRoot?.querySelector('button[data-tool-id="fit"]')?.click();

    const style = document.createElement("style");
    style.textContent = runtimeStyles;
    this.shadowRoot?.append(style);

    const renderPanel = (animateBranchId = null) => {
      this.#hideTooltip();
      root.replaceChildren();
      this.#ensureLazySwatchObserver(root);
      const active = new Set(this.#session.state.activeBranchIds);
      for (const branch of branches.filter((candidate) => active.has(candidate.id))) {
        const selectedId = this.#session.state.selections[branch.id];
        const selected = branch.options.find((option) => option.id === selectedId);
        const isShaderBranch = branch.options.some(
          (option) => Array.isArray(option.selectedShaders) && option.selectedShaders.length > 0,
        );
        const optionSubPath = (option) => Array.isArray(option?.subPath) && option.subPath.length
          ? option.subPath
          : typeof option?.shaderGroupId === "string" && option.shaderGroupId
            ? [{ id: option.shaderGroupId, label: option.shaderGroupLabel || option.shaderGroupId }]
            : typeof option?.familyId === "string" && option.familyId
              ? [{ id: option.familyId, label: option.familyLabel || option.familyId }]
            : [];
        const families = [...new Map(branch.options.flatMap((option) => {
          const path = optionSubPath(option);
          const leaf = path.at(-1);
          return leaf ? [[leaf.id, { id: leaf.id, label: leaf.label || leaf.id, path }]] : [];
        })).values()];
        const selectedFamilyId = optionSubPath(
          branch.options.find((option) => option.id === selectedId),
        ).at(-1)?.id;
        const activeFamilyId = this.#familyByBranch.get(branch.id)
          ?? selectedFamilyId
          ?? families[0]?.id
          ?? null;
        const familyOptions = activeFamilyId
          ? branch.options.filter((option) => optionSubPath(option).at(-1)?.id === activeFamilyId)
          : branch.options;
        const expanded = this.#openBranchId === branch.id;
        const section = document.createElement("section");
        section.part = "branch";
        section.className = "expander-branch";
        const header = document.createElement("button");
        header.type = "button";
        header.part = "branch-header";
        header.className = "expander-header";
        header.setAttribute("aria-expanded", String(expanded));
        header.innerHTML = `<span class="expander-title">${escapeHtml(branch.label ?? branch.id)}</span><span class="expander-selected">${escapeHtml(selected?.label ?? selected?.id ?? "")}</span><svg class="expander-chevron" viewBox="0 0 20 20" aria-hidden="true"><path d="m5.5 7.5 4.5 4.5 4.5-4.5"/></svg>`;
        let options = null;
        header.addEventListener("click", () => {
          void (async () => {
            const transition = ++this.#transition;
            if (expanded) {
              await animateCollapsible(options, false);
              if (transition !== this.#transition) return;
              this.#openBranchId = null;
              renderPanel();
              return;
            }

            const current = root.querySelector(".expander-options");
            await animateCollapsible(current, false);
            if (transition !== this.#transition) return;
            if (isShaderBranch && !this.#pageByBranch.has(branch.id)) {
              if (activeFamilyId) this.#familyByBranch.set(branch.id, activeFamilyId);
              const selectedIndex = Math.max(0, familyOptions.findIndex((option) => option.id === selectedId));
              this.#pageByBranch.set(branch.id, Math.floor(selectedIndex / SHADER_PAGE_SIZE));
            }
            this.#openBranchId = branch.id;
            renderPanel(branch.id);
          })();
        });
        section.append(header);

        if (expanded) {
          options = document.createElement("div");
          options.part = "options";
          options.className = "expander-options";

          if (isShaderBranch && families.length > 1 && subMode === "dropdown") {
            const family = document.createElement("select");
            family.className = "expander-family";
            family.setAttribute("aria-label", `${branch.label} sub selection`);
            for (const entry of families) {
              const choice = document.createElement("option");
              choice.value = entry.id;
              choice.textContent = entry.path.map((part) => part.label).join(" / ");
              choice.selected = entry.id === activeFamilyId;
              family.append(choice);
            }
            family.addEventListener("change", () => {
              this.#familyByBranch.set(branch.id, family.value);
              this.#pageByBranch.set(branch.id, 0);
              renderPanel();
            });
            options.append(family);
          }
          if (isShaderBranch && families.length > 1 && subMode === "expanders") {
            const familyList = document.createElement("div");
            familyList.className = "expander-family-expanders";
            for (const entry of families) {
              const family = document.createElement("button");
              family.type = "button";
              family.className = "expander-family-expander";
              family.style.paddingInlineStart = `${10 + Math.max(0, entry.path.length - 1) * 14}px`;
              family.setAttribute("aria-expanded", String(entry.id === activeFamilyId));
              family.innerHTML = `<span>${escapeHtml(entry.path.map((part) => part.label).join(" / "))}</span><svg class="expander-family-expander-chevron" viewBox="0 0 20 20" aria-hidden="true"><path d="m5.5 7.5 4.5 4.5 4.5-4.5"/></svg>`;
              family.addEventListener("click", () => {
                this.#familyByBranch.set(branch.id, entry.id);
                this.#pageByBranch.set(branch.id, 0);
                renderPanel();
              });
              familyList.append(family);
            }
            options.append(familyList);
          }

          const pageCount = Math.max(1, Math.ceil(familyOptions.length / SHADER_PAGE_SIZE));
          const requestedPage = this.#pageByBranch.get(branch.id) ?? 0;
          const pageIndex = Math.min(Math.max(0, requestedPage), pageCount - 1);
          this.#pageByBranch.set(branch.id, pageIndex);
          const visibleOptions = isShaderBranch
            ? familyOptions.slice(pageIndex * SHADER_PAGE_SIZE, (pageIndex + 1) * SHADER_PAGE_SIZE)
            : branch.options;
          const optionContainer = document.createElement("div");
          if (isShaderBranch) optionContainer.className = "expander-shader-grid";

          const selectOption = (option) => {
            const state = this.#session.select(branch.id, option.id);
            this.emit("vtx:selection-change", { branchId: branch.id, optionId: option.id, state });
            const shaders = [...new Set([...this.#baseSelectedShaders, ...state.selectedShaders])];
            void this.applyRuntimeSelection(state.selectedMeshes, shaders, state.selectedTransforms)
              ?.then(() => this.emit("vtx:scene-applied", { branchId: branch.id, optionId: option.id, state }))
              .catch((reason) => this.emit("vtx:error", { client: "expanders", message: reason instanceof Error ? reason.message : String(reason) }));

            const transition = ++this.#transition;
            void animateCollapsible(options, false).then(() => {
              if (transition !== this.#transition) return;
              this.#openBranchId = null;
              renderPanel();
            });
          };

          for (const option of visibleOptions) {
            const button = document.createElement("button");
            const isSelected = selectedId === option.id;
            button.type = "button";
            button.part = "option";
            button.setAttribute("aria-pressed", String(isSelected));
            button.setAttribute("aria-label", option.label ?? option.id);

            const shaderId = Array.isArray(option.selectedShaders) ? option.selectedShaders[0] : null;
            if (isShaderBranch && shaderId) {
              button.className = "expander-shader-option";
              const swatch = document.createElement("span");
              swatch.className = "expander-swatch";
              swatch.setAttribute("aria-hidden", "true");
              this.#queueShaderSwatch(manifest, shaderId, swatch);
              button.append(swatch);

              if (isSelected) {
                const check = document.createElement("span");
                check.className = "expander-shader-check";
                check.setAttribute("aria-hidden", "true");
                check.textContent = "✓";
                button.append(check);
              }

              const label = option.label ?? option.id;
              button.addEventListener("mouseenter", () => this.#showTooltip(label, button));
              button.addEventListener("mouseenter", () => {
                const image = swatch.querySelector("img[data-detail-src]");
                if (image) image.src = image.dataset.detailSrc;
              });
              button.addEventListener("mouseleave", () => {
                this.#hideTooltip(button);
                const image = swatch.querySelector("img[data-thumbnail-src]");
                if (image) image.src = image.dataset.thumbnailSrc;
              });
              button.addEventListener("focus", () => this.#showTooltip(label, button));
              button.addEventListener("focus", () => {
                const image = swatch.querySelector("img[data-detail-src]");
                if (image) image.src = image.dataset.detailSrc;
              });
              button.addEventListener("blur", () => {
                this.#hideTooltip(button);
                const image = swatch.querySelector("img[data-thumbnail-src]");
                if (image) image.src = image.dataset.thumbnailSrc;
              });
            } else {
              button.className = "expander-option";
              const check = document.createElement("span");
              check.className = "expander-check";
              check.setAttribute("aria-hidden", "true");
              check.textContent = isSelected ? "✓" : "";
              const label = document.createElement("span");
              label.textContent = option.label ?? option.id;
              button.append(check, label);
            }

            button.addEventListener("click", () => selectOption(option));
            optionContainer.append(button);
          }

          options.append(optionContainer);
          if (isShaderBranch && familyOptions.length > SHADER_PAGE_SIZE) {
            options.append(this.#buildPagination(
              branch.id,
              pageIndex,
              familyOptions.length,
              () => renderPanel(),
            ));
          }
          section.append(options);
          if (animateBranchId === branch.id) {
            requestAnimationFrame(() => void animateCollapsible(options, true));
          }
        }
        root.append(section);
      }
    };
    renderPanel();
    this.emit("vtx:selection-change", { reason: "initial", state: this.#session.state });
  }
}

const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character]);

export function registerVertcieExpanders() {
  if (!globalThis.customElements) return;
  if (!customElements.get(EXPANDERS_ELEMENT)) customElements.define(EXPANDERS_ELEMENT, VertcieExpandersElement);
}

registerVertcieExpanders();
